import { useCallback, useEffect, useRef } from 'react';
import {
  type EmbeddedPaymentElementConfiguration,
  type EmbeddedPaymentElementResult,
  type IntentConfiguration,
} from '@stripe/stripe-react-native';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

import { type PaymentMethod } from '../../types';
import {
  fallbackNoPaymentIsNeeded,
  fallbackToSavedPaymentMethod,
  StripeLogger,
} from '../../utils';
import { useNotifyStripeCancel } from '../useNotifyStripeCancel';
import { useNotifyStripeError } from '../useNotifyStripeError';
import { useStripePayment } from '../useStripePayment';

/**
 * Order submission includes playback, confirmation and submission.
 * Confirmation -> Creating and confirming payment intent OR creating payment method.
 * Playback -> Showing order playback (customer can go back).
 * Submission -> Calling the submission mutation to the backend.
 * When {isPaymentStatePollingEnabled}, it follows this flow: Playback -> Confirmation -> Submission
 * When it is disabled, it follows this flow: Confirmation -> Playback -> Submission
 */
export const useStripeIntegration = (props: UseStripeIntegrationProps) => {
  const {
    paymentMethods,
    total,
    stripeIntentConfig,
    stripeElementConfig,
    startOrderPolling,
    requestOrderSubmission,
    requestOrderPlayback,
    cancelRequest,
    startStripeConfirmation,
    cancelPreviousIntent,
    flagFailureAsShown,
  } = props;

  const isPaymentStatePollingEnabled = useFeatureFlag('CELS-4828-order-payment-state-polling-enabled'); // prettier-ignore

  // ─── Telemetry And Notifications ───────────────────────────────────────────

  const trackStripeUsage = useStripeUsageTracking();
  const notifyStripeError = useNotifyStripeError(trackStripeUsage);
  const notifyStripeCancel = useNotifyStripeCancel();

  // ─── Stripe SDK ────────────────────────────────────────────────────────────

  const stripeSdkResponse = useStripePayment({
    isPaymentStatePollingEnabled,
    total,
    paymentMethods,
    stripeIntentConfig,
    stripeElementConfig,
  });

  const { stripeError, selectedPaymentMethod, confirmStripe } =
    stripeSdkResponse;

  useEffect(() => {
    if (stripeError) notifyStripeError(stripeError);
  }, [stripeError, notifyStripeError]);

  // ─── Callbacks ─────────────────────────────────────────────────────────────

  // This is called by Stripe after the payment intent is confirmed or the payment method is created.
  // When {isPaymentStatePollingEnabled} it must start polling the order payment state.
  // Otherwise it must initiate order playback.
  // If there is no total or the SDK is having issues, we'll attempt to submit the order with a payment method.
  const handleStripeConfirmation = useCallback(
    (params: {
      shouldSubmitWithPaymentMethod: boolean;
      paymentIntentOrMethodId?: string;
    }) => {
      const { shouldSubmitWithPaymentMethod, paymentIntentOrMethodId } = params;

      if (shouldSubmitWithPaymentMethod) {
        requestOrderSubmission(paymentIntentOrMethodId);
        return;
      }

      if (isPaymentStatePollingEnabled) {
        startOrderPolling(paymentIntentOrMethodId);
        return;
      }

      requestOrderPlayback(paymentIntentOrMethodId);
    },
    [
      requestOrderPlayback,
      requestOrderSubmission,
      startOrderPolling,
      isPaymentStatePollingEnabled,
    ],
  );

  // ─── Payment Confirmation ─────────────────────────────────────────────────

  const stableRefs = useStableRefs({
    cancelRequest,
    cancelPreviousIntent,
    startStripeConfirmation,
    handleStripeConfirmation,
    selectedPaymentMethod,
    paymentMethods,
    total,
    confirmStripe,
    trackStripeUsage,
    notifyStripeError,
    notifyStripeCancel,
    flagFailureAsShown,
  });

  const confirmPayment = useCallback(async () => {
    const {
      selectedPaymentMethod,
      paymentMethods,
      total,
      startStripeConfirmation,
      cancelRequest,
      cancelPreviousIntent,
      handleStripeConfirmation,
      confirmStripe,
      trackStripeUsage,
      notifyStripeError,
      notifyStripeCancel,
      flagFailureAsShown,
    } = stableRefs.current;

    startStripeConfirmation();

    // Use first saved payment method if no payment method is selected from the SDK.
    if (!selectedPaymentMethod && paymentMethods.length > 0) {
      trackStripeUsage({ event: 'confirming_with_fallback' });
      handleStripeConfirmation({
        shouldSubmitWithPaymentMethod: true,
        paymentIntentOrMethodId: fallbackToSavedPaymentMethod(paymentMethods)?.id, // prettier-ignore
      });

      return;
    }

    // Use first saved payment method or fallback to none if the total is zero.
    if (total === 0) {
      trackStripeUsage({ event: 'confirming_with_zero_total' });
      handleStripeConfirmation({
        shouldSubmitWithPaymentMethod: true,
        paymentIntentOrMethodId: fallbackNoPaymentIsNeeded(paymentMethods)?.id, // prettier-ignore
      });

      return;
    }

    const result = (await confirmStripe()) as EmbeddedPaymentElementResult & {
      paymentMethodId: string;
    };

    StripeLogger.logConfirmResult(result);

    // Start polling the order payment state when the payment intent is confirmed.
    if (result.status === 'completed') {
      trackStripeUsage({ event: 'result_completed', result });
      handleStripeConfirmation({
        shouldSubmitWithPaymentMethod: false,
        paymentIntentOrMethodId: result.paymentMethodId,
      });
      return;
    }

    if (result.status === 'failed') {
      StripeLogger.logConfirmError(result.error);
      trackStripeUsage({ event: 'result_failed', result });
      notifyStripeError(result.error.message ?? result.error);
      cancelRequest(true);
      flagFailureAsShown();
      return;
    }

    // If the payment intent is flagged as canceled, also call the cancel mutation to cancel the payment state.
    if (result.status === 'canceled') {
      StripeLogger.logCancelation();
      cancelPreviousIntent();
      trackStripeUsage({ event: 'result_canceled', result });
      notifyStripeCancel();
      cancelRequest(true);
      flagFailureAsShown();
    }
  }, [stableRefs]);

  return { ...stripeSdkResponse, confirmPayment };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * To avoid re-rendering the Stripe SDK by changing the callback that it receives.
 */
const useStableRefs = (props: UseStableRefsProps) => {
  const {
    cancelRequest,
    cancelPreviousIntent,
    startStripeConfirmation,
    handleStripeConfirmation,
    selectedPaymentMethod,
    paymentMethods,
    total,
    confirmStripe,
    trackStripeUsage,
    notifyStripeError,
    notifyStripeCancel,
    flagFailureAsShown,
  } = props;

  const stableRefs = useRef({
    cancelRequest,
    cancelPreviousIntent,
    startStripeConfirmation,
    handleStripeConfirmation,
    selectedPaymentMethod,
    paymentMethods,
    total,
    confirmStripe,
    trackStripeUsage,
    notifyStripeError,
    notifyStripeCancel,
    flagFailureAsShown,
  });

  useEffect(() => {
    stableRefs.current = {
      cancelRequest,
      cancelPreviousIntent,
      startStripeConfirmation,
      handleStripeConfirmation,
      selectedPaymentMethod,
      paymentMethods,
      total,
      confirmStripe,
      trackStripeUsage,
      notifyStripeError,
      notifyStripeCancel,
      flagFailureAsShown,
    };
  }, [
    selectedPaymentMethod,
    paymentMethods,
    total,
    startStripeConfirmation,
    cancelRequest,
    cancelPreviousIntent,
    handleStripeConfirmation,
    confirmStripe,
    trackStripeUsage,
    notifyStripeError,
    notifyStripeCancel,
    flagFailureAsShown,
  ]);

  return stableRefs;
};

const useStripeUsageTracking = () => {
  const { track } = useTelemetry();

  const trackStripeUsage = useCallback(
    (payload: Record<string, unknown>) => {
      track('checkout.stripe-usage', payload);
    },
    [track],
  );

  return trackStripeUsage;
};

// ─── Types ──────────────────────────────────────────────────────────────────

type UseStripeIntegrationProps = {
  paymentMethods: readonly PaymentMethod[];
  total: number;
  stripeIntentConfig: IntentConfiguration | null;
  stripeElementConfig: EmbeddedPaymentElementConfiguration | null;
  startOrderPolling: (paymentMethodId?: string) => void;
  requestOrderSubmission: (paymentMethodId?: string) => void;
  requestOrderPlayback: (paymentMethodId?: string) => void;
  cancelRequest: (shouldCancel: boolean) => void;
  cancelPreviousIntent: () => void;
  startStripeConfirmation: () => void;
  flagFailureAsShown: () => void;
};

type HandleStripeConfirmationParams = {
  shouldSubmitWithPaymentMethod: boolean;
  paymentIntentOrMethodId?: string;
};

type UseStableRefsProps = {
  cancelRequest: (shouldCancel: boolean) => void;
  cancelPreviousIntent: () => void;
  startStripeConfirmation: () => void;
  handleStripeConfirmation: (params: HandleStripeConfirmationParams) => void;
  selectedPaymentMethod: string | undefined;
  paymentMethods: readonly PaymentMethod[];
  total: number;
  confirmStripe: () => Promise<EmbeddedPaymentElementResult>;
  trackStripeUsage: (payload: Record<string, unknown>) => void;
  notifyStripeError: (error: string) => void;
  notifyStripeCancel: () => void;
  flagFailureAsShown: () => void;
};
